/**
 * Italian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: tu (locked in l10n/locales.md).
 *
 * Platform terms follow Italian UI conventions (impostazioni,
 * associare for pairing, duplicazione for notification mirroring —
 * parallel to fr, cancellare for dismiss per Android-it "Cancella
 * tutto", Appunti for clipboard). "Glance" is the untranslated feature
 * brand name (glossary D1); "clip" is the D2 loanword (natural in it).
 *
 * Plural notes: Italian cardinal categories are {one, many, other} with
 * `many` millions-only — `selectPlural` calls supply {one, other}.
 * Italian `one` is exactly 1 (unlike fr/pt), so "0 risultati" renders
 * plural, the standard Italian behavior.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Connesso",
    statusReconnecting: "Riconnessione…",
    statusDisconnected: "Disconnesso",
    openSettings: "Apri impostazioni",
    pairedFallbackDevice: "il tuo telefono",
    publicModeToggleEnable: "Nascondi il contenuto delle notifiche",
    publicModeToggleDisable: "Mostra il contenuto delle notifiche",
  },

  notifications: {
    replyAriaLabel: "Rispondi",
    replyPlaceholder: "Rispondi…",
    replySend: "Invia",
    sent: "Inviato",
    failedToSend: "Invio non riuscito",
    unread: "Da leggere",
    dismiss: "Cancella",
    notificationImageAlt: "Immagine della notifica",
  },

  conversations: {
    replyAriaLabel: "Rispondi",
    replyPlaceholder: "Rispondi…",
    replySend: "Invia",
    sent: "Inviato",
    failedToSend: "Invio non riuscito",
    dismiss: "Cancella conversazione",
    messagesListLabel: "Messaggi di questa conversazione",
    deviceOwner: "Tu",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Nessun messaggio da leggere"
        : `${count} ${selectPlural("it", count, {
            one: "messaggio da leggere",
            other: "messaggi da leggere",
          })}`,
  },

  list: {
    today: "Oggi",
    yesterday: "Ieri",
    searchNotifications: "Cerca notifiche",
    clearSearch: "Cancella ricerca",
    clearAll: "Cancella tutto",
    markAllRead: "Segna tutte come lette",
    groupCheckboxLabel: "Raggruppa",
    noNotificationsTitle: "Ancora nessuna notifica",
    noNotificationsDesc: "I nuovi messaggi duplicati dal tuo telefono appariranno qui.",
    noMatchesTitle: "Nessun risultato",
    notificationsList: "Notifiche",
    privacyAppList: "Notifiche per app",
    clear: "Cancella",
    clearMatches: (count: number): string =>
      `Cancella ${count} ${selectPlural("it", count, { one: "risultato", other: "risultati" })}`,
    noMatchesDesc: (query: string): string => `Nessuna notifica corrisponde a "${query}".`,
    appNotificationsGroup: (appName: string): string => `Notifiche di ${appName}`,
    dismissAllFrom: (appName: string): string => `Cancella tutte le notifiche di ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("it", count, { one: "messaggio", other: "messaggi" })
          : kind === "calls"
            ? selectPlural("it", count, { one: "chiamata", other: "chiamate" })
            : selectPlural("it", count, { one: "notifica", other: "notifiche" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "cancella",
    kbdNavigate: "naviga",
    kbdOrSeparator: "o",
  },

  settings: {
    back: "Torna alle notifiche",
    settings: "Impostazioni",
    appearance: "Aspetto",
    theme: "Tema",
    themeAuto: "Automatico",
    themeLight: "Chiaro",
    themeDark: "Scuro",
    language: "Lingua",
    languageSystemDefault: "Predefinita di sistema",
    notificationsSection: "Notifiche",
    osNotifications: "Notifiche di sistema",
    callNotifications: "Notifiche di chiamata",
    privacySection: "Privacy",
    analyticsToggle: "Statistiche di utilizzo anonime",
    analyticsToggleDescription: "Solo conteggi e traguardi — mai le tue notifiche.",
    connection: "Connessione",
    status: "Stato",
    statusConnected: "Connesso",
    statusReconnecting: "Riconnessione…",
    statusDisconnected: "Disconnesso",
    disconnectFromPhone: "Disconnetti dal telefono",
    disconnectConfirm: "Vuoi davvero farlo? Il dispositivo verrà dissociato.",
    yesDisconnect: "Sì, disconnetti",
    cancel: "Annulla",
    aboutAndSupport: "Informazioni e assistenza",
    version: "Versione",
    encryption: "Crittografia",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Ti serve aiuto? Scrivi a __EMAIL__",
    disconnectFailed: "Disconnessione non riuscita: riprova",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Disconnesso, ma la pulizia locale non è riuscita: ${errorMsg}. Ricarica la pagina.`,
    disconnectLocalCleanupFailed:
      "Disconnesso, ma la pulizia locale non è riuscita. Ricarica la pagina.",
  },

  pairing: {
    enterCodeInstruction: "Inserisci questo codice nell'app Reflecto",
    pairedSuccessfully: "Associazione riuscita!",
    pairingFailed: "Associazione non riuscita",
    requestingPairingCode: "Richiesta del codice di associazione…",
    codeExpired: "Codice scaduto",
    tryAgain: "Riprova",
    failedToStartPairing: "Impossibile avviare l'associazione",
    needAppText: "Ti serve l'app Android? Scrivi a __EMAIL__ per ottenere l'accesso.",
  },

  dnd: {
    title: "Non disturbare",
    description: "Sospendi le notifiche su questo dispositivo per la prossima ora.",
    snooze1h: "Sospendi per 1 ora",
    snoozeActiveUntil: (formattedTime: string): string => `Attivo fino alle ${formattedTime}`,
    cancelSnooze: "Annulla",
  },

  connectors: {
    title: "Servizi collegati",
    emptyBody:
      "Ancora nessun servizio collegato. Collega ChatGPT o Claude dalle loro impostazioni dei connettori, poi approva il codice sul telefono.",
    scopeSend: "Può inviare notifiche",
    added: (date: string): string => `Aggiunto ${date}`,
    lastUsed: (relative: string): string => `Ultimo utilizzo ${relative}`,
    neverUsed: "Mai usato",
    revoke: "Scollega",
    revokeConfirm: (label: string): string =>
      `Scollegare ${label}? Perderà l'accesso immediatamente.`,
    revokeAction: "Scollega",
    revokeConfirmLabel: (label: string): string => `Scollega ${label}`,
    cancel: "Annulla",
    loadError: "Impossibile caricare i servizi collegati. Riprova.",
    revokeError: "Impossibile scollegare il servizio. Riprova.",
    retry: "Riprova",
  },

  feedback: {
    title: "Invia feedback",
    close: "Chiudi",
    messageLabel: "Il tuo messaggio",
    messagePlaceholder: "Hai trovato un bug, hai un'idea o qualcosa non ti convince? Raccontacelo.",
    categoryLabel: "Categoria",
    categoryBug: "Bug",
    categoryIdea: "Idea",
    categoryOther: "Altro",
    emailLabel: "E-mail (facoltativa, solo se vuoi una risposta)",
    emailPlaceholder: "tu@esempio.it",
    emailHelp: "Non ti aggiungeremo a nessuna lista.",
    disclosure:
      "Il tuo messaggio viene inviato al server di Reflecto e registrato nel nostro sistema interno di tracciamento. Alleghiamo la versione dell'app e un ID dispositivo assegnato durante l'associazione per correlare le segnalazioni. Non vediamo le tue notifiche, i contatti né alcun dato personale. L'e-mail è facoltativa: indicala solo se vuoi una risposta. Non ti aggiungeremo a nessuna lista.",
    submit: "Invia feedback",
    sending: "Invio in corso…",
    success: "Grazie! Verrà letto da una persona, di solito entro 24 ore",
    error: "Invio non riuscito. Riprova.",
    done: "Fine",
    sendFeedback: "Invia feedback",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Chiudi",
    aiSummaryTag: "Riepilogo IA",
    errorAi: "Impossibile generare un riepilogo al momento.",
    retryCta: "Riprova",
    downloadHeading: "Ricevi riepiloghi IA",
    downloadBody:
      "Recupera ciò che hai perso con un riepilogo IA generato sul dispositivo. Tutto gira sul tuo dispositivo.",
    downloadCta: "Scarica il modello",
    downloadPrivacyNote: "Un solo download. Il server non vede mai le tue notifiche.",
    downloadingHeading: "Download del modello…",
    caughtUp: "Sei in pari: al momento niente richiede la tua attenzione.",
    needsYou: "Da gestire",
    quiet: "Il resto",
    scanning: (pending: number): string => `Riepilogo di ${pending} in corso…`,
    jump: "Apri notifica",
    affordanceReply: "Rispondi",
    affordanceOpen: "Apri",
    affordanceCopyOtp: "Copia codice",
    buttonLabel: "Glance",
  },

  update: {
    title: "Aggiornamento disponibile",
    description: "Una nuova versione di Reflecto è pronta. Ricarica per aggiornare.",
    reload: "Ricarica",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Abbiamo chiamato questo dispositivo "${name}".`,
    cardLooksGood: "Va bene",
    cardEdit: "Modifica",
    cardAriaLabel: "Conferma il nome del dispositivo",
    renameTitle: "Rinomina questo dispositivo",
    renameLabel: "Nome del dispositivo",
    renameSave: "Salva",
    renameCancel: "Annulla",
    renameFailed: "Impossibile rinominare. Riprova.",
  },

  collision: {
    title: (name: string): string => `Hai già un "${name}"`,
    subtitle: "Come vuoi chiamare questo?",
    chipWork: (name: string): string => `Lavoro ${name}`,
    chipHome: (name: string): string => `Casa ${name}`,
    chipPersonal: (name: string): string => `Personale ${name}`,
    customLabel: "Nome personalizzato",
    save: "Salva",
    useAnyway: "Usa comunque il nome originale",
    saveFailed: "Impossibile salvare il nuovo nome. Riprova.",
    ariaLabel: "Risolvi il nome dispositivo duplicato",
  },

  clipboard: {
    title: "Appunti",
    empty: "Le clip dagli altri tuoi dispositivi appaiono qui. Tocca per copiare.",
    from: (label: string): string => `da ${label}`,
    copy: "Copia negli appunti",
    copied: "Copiato",
    reveal: "Mostra",
    hide: "Nascondi",
    sensitiveChip: "Sensibile",
    cloudChip: "dal tuo token API",
    maskedHint: 'Contenuto nascosto: premi "Mostra" per vederlo',
    sendPlaceholder: "Incolla il testo da inviare…",
    sendCta: "Invia",
    sendSensitiveToggle: "Contrassegna come sensibile",
    sendSuccess: (n: number): string =>
      `Inviato a ${n} ${selectPlural("it", n, { one: "dispositivo", other: "dispositivi" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Inviato a ${n} ${selectPlural("it", total, {
        one: "dispositivo su",
        other: "dispositivi su",
      })} ${total}.`,
    sendNoDevices: "Nessun dispositivo associato a cui inviare.",
    sendFailed: "Invio non riuscito. Riprova.",
    sendEmpty: "Niente da inviare.",
    dockExpand: "Mostra la cronologia delle clip",
    dockCollapse: "Comprimi",
    dockSendTitle: "Invia ai tuoi dispositivi",
    dockFromClipboard: (chars: number): string =>
      `Dagli appunti · ${chars} ${selectPlural("it", chars, {
        one: "carattere",
        other: "caratteri",
      })}`,
    dockSend: (n: number): string => `Invia a ${n}`,
    dockCancel: "Annulla",
    dockRecentHeading: "Recenti",
    dockEmptyClipboard: "I tuoi appunti sono vuoti.",
    shareTitle: "Invia ai tuoi dispositivi",
    shareReview:
      "Controlla cosa stai inviando. Andrà ai tuoi dispositivi associati, crittografato end-to-end.",
    shareSend: "Invia",
    shareCancel: "Annulla",
    shareNotPaired: "Associa prima un dispositivo per inviare il testo condiviso.",
    shareOpenApp: "Apri Reflecto",
  },
  install: {
    title: "Installa Reflecto",
    body: "Aggiungi alla schermata Home per un accesso più rapido.",
    cta: "Installa",
    dismissAria: "Chiudi la richiesta di installazione",
  },
  common: {
    loading: "Caricamento",
  },
  errors: {
    notPaired: "Non associato",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} su ${os}`,
    webFallback: "Browser web",
  },
  palette: {
    placeholder: "Digita un comando…",
    noResults: "Nessun comando corrispondente",
    ariaLabel: "Palette dei comandi",
    clearAll: "Cancella tutte le notifiche",
    glance: "Riepilogo Glance",
    cancelSnooze: "Annulla sospensione",
    copyClip: "Copia l'ultima clip ricevuta",
  },
} satisfies MessagesShape;
